import * as Buttons from "./Buttons";
import * as Sliders from "./Sliders";
import * as Tooltip from "@radix-ui/react-tooltip";

import { Captions, Controls, Gesture, useMediaState } from "@vidstack/react";
import { PlayNextButton, PlayPreviousButton } from "../SkipButtons";

import BusinessCard from "../player/shared/BusinessCard";
import { TimeGroup } from "./TimeGroup";
import captionStyles from "./captions.module.css";
import { cn } from "@/lib/utils";
import { useDataContext } from "../../app/_context";

// Offset tooltips/menus/slider previews in the lower controls group so they're clearly visible.
const popupOffset = 30;

export interface VideoLayoutProps {
  thumbnails?: string;
}

const dev = false;

export function VideoLayout({ thumbnails }: VideoLayoutProps) {
  const { isPortal, isBusinessCardPlaying } = useDataContext();
  const isFullScreen = useMediaState("fullscreen");

  if (!isBusinessCardPlaying)
    return (
      <>
        <Gestures />
        <Captions
          className={`${captionStyles.captions} media-preview:opacity-0 media-controls:bottom-[85px] media-captions:opacity-100 absolute inset-0 bottom-2 z-10 select-none break-words opacity-0 transition-[opacity,bottom] duration-300`}
        />
        <Controls.Root
          className={cn(
            isPortal ? "fixed pb-8" : "absolute",
            !dev
              ? "group-hover:opacity-100 opacity-0 transition-opacity"
              : "opacity-100",
            "media-controls:opacity-100 inset-0 z-10 flex h-full w-full flex-col bg-gradient-to-t via-transparent  from-black/80 to-transparent"
          )}
        >
          <Tooltip.Provider>
            {isFullScreen && (
              <div className="absolute z-[9999] mb-[100px] [&>div]:p-4 [&>div]:opacity-0 [&>div]:hover:opacity-100 [&>div]:flex [&>div]:items-center pointer-events-none *:pointer-events-auto inset-0 flex justify-between">
                <div className="bg-gradient-to-l fadeEdges from-transparent to-black pl-12">
                  <PlayPreviousButton />
                </div>
                <div className="bg-gradient-to-r fadeEdges from-transparent to-black pr-12">
                  <PlayNextButton />
                </div>
              </div>
            )}
            <div className="flex-1 z-10" />
            {!isPortal && (
              <Controls.Group className="flex w-full items-center px-2">
                <Sliders.Time thumbnails={thumbnails} />
              </Controls.Group>
            )}
            <Controls.Group className="-mt-0.5 flex w-full items-center px-2 pb-2">
              {!isPortal && (
                <Buttons.Play
                  tooltipAlign="start"
                  tooltipOffset={popupOffset}
                />
              )}
              <Buttons.Mute tooltipOffset={popupOffset} />
              {!isPortal && <Sliders.Volume />}{" "}
              {isPortal && (
                <Controls.Group className="flex w-full items-center px-4">
                  <Sliders.Time thumbnails={thumbnails} />
                </Controls.Group>
              )}
              <TimeGroup />
              {!isPortal && <Buttons.FrameStep tooltipOffset={popupOffset} />}
              {/* <Title /> */}
              {!isPortal && <div className="flex-1" />}
              {/* <Menus.Captions offset={popupOffset} tooltipOffset={popupOffset} /> */}
              {!isPortal && <Buttons.PIP tooltipOffset={popupOffset} />}
              <Buttons.Fullscreen
                tooltipAlign="end"
                tooltipOffset={popupOffset}
              />
            </Controls.Group>
          </Tooltip.Provider>
        </Controls.Root>
      </>
    );
  else
    return (
      <>
        {isBusinessCardPlaying ? (
          <div className="absolute pointer-events-none inset-0 bg-zinc-800/40 group rounded-md text-white items-center flex justify-center z-20">
            <div className="absolute top-0 left-0 right-0 top-0 bottom-0">
              <BusinessCard />
            </div>
          </div>
        ) : null}
        <Gestures />
        <Controls.Root
          className={cn(
            isPortal ? "fixed pb-8" : "absolute",
            "media-controls:opacity-100 inset-0 z-10 bottom-0 flex h-full w-full flex-col"
          )}
        >
          {!isPortal && (
            <Tooltip.Provider>
              <Controls.Group className="-mt-0.5 flex w-full items-center justify-between bottom-0 absolute  px-2 pb-2">
                <Buttons.Play
                  tooltipAlign="start"
                  tooltipOffset={popupOffset}
                />
                <TimeGroup />
              </Controls.Group>
            </Tooltip.Provider>
          )}
        </Controls.Root>
      </>
    );
}

function Gestures() {
  return (
    <>
      <Gesture
        className="absolute inset-0 z-0 block h-full w-full"
        event="pointerup"
        action="toggle:paused"
      />
      <Gesture
        className="absolute inset-0 z-0 block h-full w-full"
        event="dblpointerup"
        action="toggle:fullscreen"
      />
      <Gesture
        className="absolute left-0 top-0 z-10 block h-full w-1/5"
        event="dblpointerup"
        action="seek:-10"
      />
      <Gesture
        className="absolute right-0 top-0 z-10 block h-full w-1/5"
        event="dblpointerup"
        action="seek:10"
      />
    </>
  );
}
